/**
 * Template Recommendation Service
 * Calculates best template matches based on wizard answers
 */

import { Template } from '../../../types/template';
import { templateService } from './templateService';
import { Platform, Audience, Goal, Duration, TemplateRecommendation } from '../atoms/wizardAtoms';

interface WizardAnswers {
  platform: Platform;
  audience: Audience;
  goal: Goal;
  duration: Duration;
}

const SCORING_MATRIX: Record<string, Record<string, Record<string, number>>> = {
  platform: {
    youtube: { 'content-youtube-opener': 100, 'edu-tutorial': 90, 'dev-code-tutorial': 85, 'edu-course-intro': 80 },
    linkedin: { 'biz-product-demo': 100, 'biz-company-culture': 95, 'interview-intro': 85, 'biz-pitch-deck': 80 },
    instagram: { 'content-social-promo': 100, 'edu-student-testimonial': 85, 'content-youtube-opener': 70 },
    tiktok: { 'content-social-promo': 100, 'content-youtube-opener': 80 },
    twitter: { 'content-social-promo': 90, 'biz-product-demo': 70 },
    course: { 'edu-course-intro': 100, 'edu-tutorial': 95, 'edu-student-testimonial': 80 },
    internal: { 'biz-company-culture': 90, 'interview-remote-setup': 85, 'interview-intro': 75 },
    multiple: {}
  },
  audience: {
    customers: { 'biz-product-demo': 100, 'edu-tutorial': 85, 'content-social-promo': 75 },
    investors: { 'biz-pitch-deck': 100, 'biz-product-demo': 70, 'biz-company-culture': 60 },
    students: { 'edu-course-intro': 100, 'edu-tutorial': 95, 'edu-student-testimonial': 80 },
    team: { 'biz-company-culture': 90, 'interview-remote-setup': 80, 'interview-intro': 75 },
    public: { 'content-youtube-opener': 90, 'content-social-promo': 85, 'biz-company-culture': 70 },
    developers: { 'dev-code-tutorial': 100, 'dev-api-docs': 95, 'edu-tutorial': 75 }
  },
  goal: {
    teach: { 'edu-tutorial': 100, 'edu-course-intro': 90, 'dev-code-tutorial': 85 },
    demo: { 'biz-product-demo': 100, 'dev-api-docs': 80, 'edu-tutorial': 75 },
    sell: { 'biz-pitch-deck': 100, 'content-social-promo': 90, 'biz-product-demo': 85 },
    introduce: { 'interview-intro': 100, 'biz-company-culture': 85, 'edu-course-intro': 70 },
    document: { 'dev-api-docs': 100, 'dev-code-tutorial': 90, 'edu-tutorial': 75 },
    testimonial: { 'edu-student-testimonial': 100, 'interview-intro': 70 }
  },
  duration: {
    quick: { 'content-social-promo': 100, 'content-youtube-opener': 95, 'interview-intro': 80 },
    standard: { 'biz-product-demo': 100, 'edu-student-testimonial': 95, 'interview-intro': 90, 'biz-company-culture': 85 },
    detailed: { 'edu-tutorial': 100, 'edu-course-intro': 95, 'dev-api-docs': 90, 'biz-pitch-deck': 85 },
    longform: { 'interview-remote-setup': 100, 'dev-code-tutorial': 85, 'edu-course-intro': 80 }
  }
};

const MATCH_REASONS: Record<string, Record<string, string>> = {
  platform: {
    youtube: 'Optimized for YouTube format',
    linkedin: 'Professional LinkedIn style',
    instagram: 'Instagram-ready format',
    tiktok: 'TikTok-optimized length',
    twitter: 'Twitter video format',
    course: 'Course platform ready',
    internal: 'Internal communications style'
  },
  audience: {
    customers: 'Customer-focused messaging',
    investors: 'Investor pitch format',
    students: 'Student-friendly structure',
    team: 'Team communication style',
    public: 'Public audience appeal',
    developers: 'Developer-focused content'
  },
  goal: {
    teach: 'Educational structure',
    demo: 'Demo-focused layout',
    sell: 'Sales-oriented flow',
    introduce: 'Introduction format',
    document: 'Documentation style',
    testimonial: 'Testimonial structure'
  },
  duration: {
    quick: 'Quick, punchy format',
    standard: 'Standard length',
    detailed: 'In-depth coverage',
    longform: 'Long-form content'
  }
};

const WEIGHTS = { platform: 25, audience: 30, goal: 35, duration: 10 };

export async function calculateRecommendations(answers: WizardAnswers): Promise<TemplateRecommendation[]> {
  const templates = await templateService.getAllTemplates();
  const scores: Record<string, { total: number; reasons: string[] }> = {};

  templates.forEach(template => {
    scores[template.id] = { total: 0, reasons: [] };
  });

  Object.entries(WEIGHTS).forEach(([dimension, weight]) => {
    const answerValue = answers[dimension as keyof WizardAnswers];
    if (!answerValue) return;
    
    const dimensionScores = SCORING_MATRIX[dimension]?.[answerValue] || {};
    
    Object.entries(dimensionScores).forEach(([templateId, score]) => {
      if (scores[templateId]) {
        scores[templateId].total += (score * weight) / 100;
        if (score >= 80) {
          const reason = MATCH_REASONS[dimension]?.[answerValue];
          if (reason && !scores[templateId].reasons.includes(reason)) {
            scores[templateId].reasons.push(reason);
          }
        }
      }
    });
  });

  const recommendations: TemplateRecommendation[] = Object.entries(scores)
    .map(([templateId, { total, reasons }]) => {
      const template = templates.find(t => t.id === templateId);
      return template ? {
        templateId,
        template,
        score: Math.round(total),
        matchReasons: reasons
      } : null;
    })
    .filter((r): r is TemplateRecommendation => r !== null && r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  return recommendations;
}

export const templateRecommendationService = {
  calculateRecommendations
};
